import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  AlertCircle,
  Check,
  X,
  Eye,
  Layers,
  Sparkles,
  Compass,
  ClipboardCheck,
  Inbox,
  MessageSquare,
} from "lucide-react";
import { Nav, Footer, Eyebrow, PillarAccent } from "../App";
import { useContent } from "../lib/useContent";
import { useSeo } from "../lib/useSeo";
import {
  HR_HERO_DEFAULTS,
  HR_WHY_DEFAULTS,
  HR_RECEIVE_DEFAULTS,
  HR_NOT_DEFAULTS,
  HR_HOW_DEFAULTS,
  HR_WHO_DEFAULTS,
  HR_FORM_DEFAULTS,
  HR_FINAL_DEFAULTS,
} from "../admin/contentDefaults";

/* ============================================================
   /homepage-review — Private Shopify Homepage Review
   Fully CMS-driven (key: "homepage_review"). Defaults imported
   from `contentDefaults.js` and merged via `useContent` so a
   blank CMS doc still renders the original copy.
============================================================ */

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const HR_DEFAULTS = {
  hero: HR_HERO_DEFAULTS,
  why: HR_WHY_DEFAULTS,
  receive: HR_RECEIVE_DEFAULTS,
  not: HR_NOT_DEFAULTS,
  how: HR_HOW_DEFAULTS,
  who: HR_WHO_DEFAULTS,
  form: HR_FORM_DEFAULTS,
  final: HR_FINAL_DEFAULTS,
};

// Static icon rotations — CMS controls copy only, not icons.
const RECEIVE_ICONS = [Compass, Layers, Sparkles, ArrowUpRight];
const HOW_ICONS = [Inbox, Eye, ClipboardCheck];

const itemTitles = (arr, fallback) => {
  if (!Array.isArray(arr) || arr.length === 0) return fallback;
  const cleaned = arr
    .map((it) => (typeof it === "string" ? it : it && it.title) || "")
    .filter(Boolean);
  return cleaned.length ? cleaned : fallback;
};

const itemObjects = (arr, fallback) => {
  if (!Array.isArray(arr) || arr.length === 0) return fallback;
  const cleaned = arr.filter(
    (it) => it && (it.title || it.body)
  );
  return cleaned.length ? cleaned : fallback;
};

/* ----- Shared atoms ----- */
const PrimaryCTA = ({ children, onClick, testId, type = "button", disabled }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-8 py-4 rounded-md transition-all duration-300 shadow-[0_12px_40px_-12px_rgba(244,214,78,0.5)] hover:shadow-[0_16px_50px_-12px_rgba(244,214,78,0.65)] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-[#F4D64E] disabled:hover:translate-y-0"
  >
    {children}
    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
  </button>
);

const TrustStrip = ({ items, center }) => {
  if (!items || items.length === 0) return null;
  return (
    <p className={`text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 flex flex-wrap ${center ? "justify-center" : ""} items-center gap-x-3 gap-y-2`}>
      {items.map((label, i) => (
        <span key={`${label}-${i}`} className="inline-flex items-center gap-x-3">
          {i > 0 ? <span className="w-1 h-1 rounded-full bg-neutral-700" /> : null}
          <span>{label}</span>
        </span>
      ))}
    </p>
  );
};

/* ============================================================
   01 · Hero
============================================================ */
const HrHero = ({ hero }) => {
  const scrollToForm = () => {
    const el = document.getElementById("review-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const trustItems = itemTitles(hero.trust_items, []);
  return (
    <section
      id="top"
      data-testid="hr-hero"
      className="relative overflow-hidden bg-[#0B0F14]"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 600px at 75% 25%, rgba(244,214,78,0.10), transparent 60%)",
        }}
      />
      <div className="relative max-w-5xl mx-auto px-6 md:px-10 pt-14 md:pt-24 pb-20 md:pb-28">
        <div
          data-reveal="eyebrow"
          className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-10 md:mb-14"
        >
          <a href="/" className="hover:text-[#F4D64E] transition-colors">Sunday</a>
          <span className="w-1 h-1 rounded-full bg-neutral-700" />
          <span className="text-[#F4D64E]">{hero.breadcrumb}</span>
        </div>

        <span data-reveal="eyebrow">
          <Eyebrow>{hero.eyebrow}</Eyebrow>
        </span>

        <h1
          data-testid="hr-hero-title"
          className="font-display mt-7 text-[2.5rem] sm:text-[3.25rem] md:text-[4rem] lg:text-[4.5rem] font-black tracking-[-0.035em] leading-[0.98] text-white max-w-[20ch]"
        >
          <span data-reveal="mask-block">
            <span>{hero.title_line_1}</span>
          </span>
          <span data-reveal="mask-block" style={{ "--i": 1 }} className="block">
            <span>
              {hero.title_line_2}{" "}
              {hero.title_accent ? (
                <>
                  <PillarAccent>{hero.title_accent}</PillarAccent>
                  <span className="text-white">.</span>
                </>
              ) : null}
            </span>
          </span>
        </h1>

        <p
          data-reveal="mask-block"
          style={{ "--i": 2 }}
          className="mt-9 max-w-[58ch] text-[1.05rem] md:text-[1.2rem] text-neutral-300 leading-[1.6]"
        >
          <span>{hero.subtitle}</span>
        </p>

        <p
          data-reveal="mask-block"
          style={{ "--i": 3 }}
          className="mt-5 max-w-[60ch] text-[0.98rem] text-neutral-400 leading-[1.7]"
        >
          <span>{hero.body}</span>
        </p>

        <div
          data-reveal="eyebrow"
          style={{ "--i": 4 }}
          className="mt-12 flex flex-col gap-4"
        >
          <div>
            <PrimaryCTA testId="hr-hero-cta" onClick={scrollToForm}>
              {hero.cta_label}
            </PrimaryCTA>
          </div>
          <TrustStrip items={trustItems} />
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   02 · Why this exists
============================================================ */
const HrWhy = ({ why }) => {
  const points = itemTitles(why.points, []);
  return (
    <section data-testid="hr-why" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <span data-reveal="eyebrow">
          <Eyebrow>{why.eyebrow}</Eyebrow>
        </span>
        <h2
          data-reveal="mask-block"
          style={{ "--i": 1 }}
          className="mt-7 font-display text-[2rem] md:text-[2.85rem] font-extrabold tracking-[-0.035em] leading-[1.04] text-white max-w-[22ch]"
        >
          <span>
            {why.headline_main}{" "}
            <span className="italic font-normal text-neutral-400">{why.headline_italic}</span>
          </span>
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-8">
          <div className="md:col-span-7">
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.7]"
            >
              <span>{why.body_1}</span>
            </p>
            <p
              data-reveal="mask-block"
              style={{ "--i": 3 }}
              className="mt-6 text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.7]"
            >
              <span>{why.body_2}</span>
            </p>
          </div>
          {points.length ? (
            <ul className="md:col-span-5 flex flex-col gap-3">
              {points.map((row, i) => (
                <li
                  key={`${row}-${i}`}
                  data-reveal="eyebrow"
                  style={{ "--i": i }}
                  className="flex items-start gap-3 text-[0.96rem] text-neutral-300 leading-[1.55] border-l border-[#F4D64E]/30 pl-5 py-1"
                >
                  <Check className="w-4 h-4 text-[#F4D64E] mt-1 shrink-0" strokeWidth={2.5} />
                  <span>{row}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   03 · What you may receive
============================================================ */
const HrReceive = ({ receive }) => {
  const cards = itemObjects(receive.cards, HR_RECEIVE_DEFAULTS.cards);
  return (
    <section data-testid="hr-receive" className="relative bg-[#11161D] border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-3xl">
          <span data-reveal="eyebrow">
            <Eyebrow>{receive.eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2rem] md:text-[2.85rem] font-extrabold tracking-[-0.035em] leading-[1.04] text-white"
          >
            <span>
              {receive.headline_main}{" "}
              <span className="italic font-normal text-neutral-400">{receive.headline_italic}</span>
            </span>
          </h2>
          {receive.intro ? (
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-6 max-w-[58ch] text-[1rem] text-neutral-400 leading-[1.7]"
            >
              <span>{receive.intro}</span>
            </p>
          ) : null}
        </div>
        <ul className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          {cards.map((card, i) => {
            const Icon = RECEIVE_ICONS[i % RECEIVE_ICONS.length];
            return (
              <li
                key={`${card.title}-${i}`}
                data-reveal="eyebrow"
                style={{ "--i": i % 3 }}
                className="relative flex flex-col px-8 pt-8 pb-12 md:pb-14 bg-[#0F141A] border border-white/[0.06] rounded-2xl overflow-hidden hover:border-[#F4D64E]/25 transition-colors duration-300"
              >
                <span
                  aria-hidden
                  className="absolute top-0 left-0 w-14 h-0.5 bg-[#F4D64E] opacity-50"
                />
                <div className="flex items-center gap-3 mb-7">
                  <span className="flex items-center justify-center w-11 h-11 rounded-full border border-[#F4D64E]/30 bg-[#F4D64E]/[0.06] text-[#F4D64E]">
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </span>
                  <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-display text-[1.25rem] md:text-[1.35rem] font-black tracking-[-0.02em] text-white leading-[1.18]">
                  {card.title}
                </h3>
                {card.body ? (
                  <p className="mt-4 text-[0.95rem] text-neutral-400 leading-[1.7]">
                    {card.body}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

/* ============================================================
   04 · What this is not
============================================================ */
const HrNot = ({ not: notData }) => {
  const bullets = itemTitles(notData.bullets, []);
  return (
    <section data-testid="hr-not" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <span data-reveal="eyebrow">
          <Eyebrow>{notData.eyebrow}</Eyebrow>
        </span>
        <h2
          data-reveal="mask-block"
          style={{ "--i": 1 }}
          className="mt-7 font-display text-[2rem] md:text-[2.85rem] font-extrabold tracking-[-0.035em] leading-[1.04] text-white max-w-[22ch]"
        >
          <span>
            {notData.headline_main}{" "}
            <span className="italic font-normal text-neutral-400">{notData.headline_italic}</span>
          </span>
        </h2>
        <p
          data-reveal="mask-block"
          style={{ "--i": 2 }}
          className="mt-6 max-w-[60ch] text-[1rem] text-neutral-400 leading-[1.7]"
        >
          <span>{notData.body}</span>
        </p>
        {bullets.length ? (
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3">
            {bullets.map((row, i) => (
              <li
                key={`${row}-${i}`}
                data-reveal="eyebrow"
                style={{ "--i": i % 3 }}
                className="flex items-start gap-3 text-[0.96rem] text-neutral-300 leading-[1.55] py-3 border-t border-white/[0.06] first:border-t-0 md:first:border-t md:[&:nth-child(2)]:border-t"
              >
                <X className="w-4 h-4 text-neutral-500 mt-1 shrink-0" strokeWidth={2.25} />
                <span>{row}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {notData.closing ? (
          <p
            data-reveal="mask-block"
            style={{ "--i": 5 }}
            className="mt-12 max-w-[60ch] text-[0.98rem] text-neutral-400 leading-[1.7] italic"
          >
            <span>{notData.closing}</span>
          </p>
        ) : null}
      </div>
    </section>
  );
};

/* ============================================================
   05 · How it works
============================================================ */
const HrHow = ({ how }) => {
  const steps = itemObjects(how.steps, HR_HOW_DEFAULTS.steps);
  return (
    <section data-testid="hr-how" className="relative bg-[#11161D] border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-3xl">
          <span data-reveal="eyebrow">
            <Eyebrow>{how.eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2rem] md:text-[2.85rem] font-extrabold tracking-[-0.035em] leading-[1.04] text-white"
          >
            <span>
              {how.headline_main}{" "}
              <span className="italic font-normal text-neutral-400">{how.headline_italic}</span>
            </span>
          </h2>
        </div>
        <ul className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {steps.map((step, i) => {
            const Icon = HOW_ICONS[i % HOW_ICONS.length];
            const n = String(i + 1).padStart(2, "0");
            return (
              <li
                key={`${step.title}-${i}`}
                data-reveal="eyebrow"
                style={{ "--i": i % 3 }}
                className="relative flex flex-col px-8 pt-8 pb-12 md:pb-14 bg-[#0F141A] border border-white/[0.06] rounded-2xl overflow-hidden"
              >
                <div className="flex items-center gap-3 mb-7">
                  <span className="inline-grid place-items-center rounded-full bg-[#F4D64E] text-neutral-950 font-display font-black text-[0.85rem] tabular-nums w-10 h-10 leading-none pt-[2px]">
                    {n}
                  </span>
                  <Icon className="w-5 h-5 text-[#F4D64E]" strokeWidth={1.75} />
                </div>
                <h3 className="font-display text-[1.25rem] md:text-[1.35rem] font-black tracking-[-0.02em] text-white leading-[1.18]">
                  {step.title}
                </h3>
                {step.body ? (
                  <p className="mt-4 text-[0.95rem] text-neutral-400 leading-[1.7]">
                    {step.body}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

/* ============================================================
   06 · Who this is for
============================================================ */
const HrWho = ({ who }) => {
  const goodItems = itemTitles(who.good_items, []);
  const notItems = itemTitles(who.not_items, []);
  return (
    <section data-testid="hr-who" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-3xl">
          <span data-reveal="eyebrow">
            <Eyebrow>{who.eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2rem] md:text-[2.85rem] font-extrabold tracking-[-0.035em] leading-[1.04] text-white"
          >
            <span>
              {who.headline_main}{" "}
              <span className="italic font-normal text-neutral-400">{who.headline_italic}</span>
            </span>
          </h2>
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="mt-6 max-w-[60ch] text-[1rem] text-neutral-400 leading-[1.7]"
          >
            <span>{who.body}</span>
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* GOOD FIT */}
          <div
            data-reveal="eyebrow"
            className="group relative px-8 pt-9 pb-12 md:px-10 md:pt-10 md:pb-14 bg-[#0F141A] border border-[#F4D64E]/30 rounded-2xl overflow-hidden"
          >
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none opacity-80"
              style={{
                background:
                  "radial-gradient(420px 240px at 0% 0%, rgba(244,214,78,0.07), transparent 60%)",
              }}
            />
            <div className="relative flex items-center justify-between gap-4 pb-7 border-b border-white/[0.06]">
              <div className="flex items-center gap-4">
                <span className="inline-grid place-items-center w-11 h-11 rounded-full bg-[#F4D64E] text-neutral-950">
                  <Check className="w-5 h-5" strokeWidth={2.5} />
                </span>
                <div className="flex flex-col">
                  <span className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
                    {who.good_label}
                  </span>
                  <span className="font-display text-[1.25rem] md:text-[1.35rem] font-black tracking-[-0.02em] text-white leading-[1.1] mt-1">
                    {who.good_subheading}
                  </span>
                </div>
              </div>
              <span className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] tabular-nums shrink-0">
                {String(goodItems.length).padStart(2, "0")}
              </span>
            </div>
            <ul className="relative mt-5 flex flex-col">
              {goodItems.map((r, i) => (
                <li
                  key={`${r}-${i}`}
                  className="flex items-start gap-3.5 text-[0.95rem] text-neutral-200 leading-[1.55] py-3.5 border-t border-white/[0.04] first:border-t-0"
                >
                  <Check className="w-4 h-4 text-[#F4D64E] mt-[3px] shrink-0" strokeWidth={2.5} />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
          {/* NOT IDEAL */}
          <div
            data-reveal="eyebrow"
            style={{ "--i": 1 }}
            className="group relative px-8 pt-9 pb-12 md:px-10 md:pt-10 md:pb-14 bg-[#0F141A]/60 border border-white/[0.07] rounded-2xl overflow-hidden"
          >
            <div className="relative flex items-center justify-between gap-4 pb-7 border-b border-white/[0.06]">
              <div className="flex items-center gap-4">
                <span className="inline-grid place-items-center w-11 h-11 rounded-full bg-white/[0.06] border border-white/10 text-neutral-400">
                  <X className="w-5 h-5" strokeWidth={2.25} />
                </span>
                <div className="flex flex-col">
                  <span className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
                    {who.not_label}
                  </span>
                  <span className="font-display text-[1.25rem] md:text-[1.35rem] font-black tracking-[-0.02em] text-neutral-300 leading-[1.1] mt-1">
                    {who.not_subheading}
                  </span>
                </div>
              </div>
              <span className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 tabular-nums shrink-0">
                {String(notItems.length).padStart(2, "0")}
              </span>
            </div>
            <ul className="relative mt-5 flex flex-col">
              {notItems.map((r, i) => (
                <li
                  key={`${r}-${i}`}
                  className="flex items-start gap-3.5 text-[0.95rem] text-neutral-400 leading-[1.55] py-3.5 border-t border-white/[0.04] first:border-t-0"
                >
                  <X className="w-4 h-4 text-neutral-500 mt-[3px] shrink-0" strokeWidth={2.25} />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   07 · Form section
============================================================ */
const FieldInput = ({
  label, name, type = "text", required, value, onChange, placeholder, testId,
  hasError, errorHint,
}) => (
  <label className="group block">
    <span className="flex items-center justify-between text-[0.7rem] font-bold tracking-[0.22em] uppercase mb-2.5">
      <span
        className={`transition-colors ${
          hasError
            ? "text-red-400"
            : "text-neutral-400 group-focus-within:text-[#F4D64E]"
        }`}
      >
        {label}
        {required && (
          <span className={`ml-1 ${hasError ? "text-red-400" : "text-[#F4D64E]"}`}>*</span>
        )}
      </span>
      {hasError && errorHint ? (
        <span className="inline-flex items-center gap-1.5 text-red-400/90 normal-case tracking-normal font-semibold text-[0.72rem]">
          <AlertCircle className="w-3.5 h-3.5" strokeWidth={2.25} />
          {errorHint}
        </span>
      ) : null}
    </span>
    <div
      className={`relative rounded-md transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        hasError
          ? "bg-red-500/[0.04] ring-1 ring-red-500/25"
          : "bg-white/[0.025] hover:bg-white/[0.04] group-focus-within:bg-white/[0.05] group-focus-within:ring-1 group-focus-within:ring-[#F4D64E]/25"
      }`}
    >
      <input
        required={required}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        aria-invalid={hasError || undefined}
        className={`w-full bg-transparent border-0 border-b-[1.5px] px-4 py-3.5 text-white text-[1.05rem] md:text-[1.1rem] placeholder:text-neutral-500 focus:outline-none transition-colors rounded-md ${
          hasError
            ? "border-red-400/70 focus:border-red-400"
            : "border-white/15 focus:border-[#F4D64E]"
        }`}
      />
    </div>
  </label>
);

const HrForm = ({ formCfg }) => {
  const platformOptions = useMemo(
    () => itemTitles(formCfg.platform_options, []),
    [formCfg.platform_options]
  );
  const goalOptions = useMemo(
    () => itemTitles(formCfg.goal_options, []),
    [formCfg.goal_options]
  );
  const revenueOptions = useMemo(
    () => itemTitles(formCfg.revenue_options, []),
    [formCfg.revenue_options]
  );
  const req = {
    message: !!formCfg.req_message,
    platform: !!formCfg.req_platform,
    goals: !!formCfg.req_goals,
    revenue: !!formCfg.req_revenue,
  };

  const [form, setForm] = useState({
    name: "",
    email: "",
    website: "",
    platform: platformOptions[0] || "",
    message: "",
    revenue_range: "",
  });
  const [goals, setGoals] = useState([]);
  const [hp, setHp] = useState("");
  const [tried, setTried] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const noteRef = useRef(null);
  const successRef = useRef(null);

  // Once the form swaps to its success state, gently scroll the
  // confirmation into view so the user actually sees the thank-you
  // message instead of landing on the section below.
  useEffect(() => {
    if (!submitted) return;
    // Wait one frame for the success section to mount.
    const id = window.requestAnimationFrame(() => {
      const el = successRef.current;
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [submitted]);

  // If the CMS option list changes (or arrives late), keep the
  // default-selected platform in sync without overwriting an admin pick.
  useEffect(() => {
    setForm((f) =>
      platformOptions.includes(f.platform) || f.platform === ""
        ? f
        : { ...f, platform: platformOptions[0] || "" }
    );
  }, [platformOptions]);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  useEffect(() => {
    const el = noteRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, 80)}px`;
  }, [form.message]);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const websiteOk = form.website.trim().length > 2;
  const errors = {
    name:    !form.name.trim() ? "Required" : "",
    email:   !form.email.trim() ? "Required" : (!emailOk ? "Invalid email" : ""),
    website: !websiteOk ? "Required" : "",
    platform: req.platform && !form.platform ? "Pick one" : "",
    goals:    req.goals && goals.length === 0 ? "Pick at least one" : "",
    revenue:  req.revenue && !form.revenue_range ? "Pick one" : "",
    message:  req.message && !form.message.trim() ? "Required" : "",
  };
  const canSubmit = Object.values(errors).every((v) => !v);
  const show = (k) => tried && !!errors[k];

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!canSubmit) {
      setTried(true);
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/homepage-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          website: form.website.trim(),
          platform: form.platform || null,
          goals,
          message: form.message.trim() || null,
          revenue_range: form.revenue_range || null,
          hp,
        }),
      });
      let data = null;
      try { data = await res.json(); } catch (_e) { /* non-JSON */ }
      if (!res.ok) {
        const msg = (data && (data.detail || data.message))
          || "We couldn't submit your request. Please try again in a moment.";
        throw new Error(typeof msg === "string" ? msg : "Submission failed.");
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section
        id="review-form"
        data-testid="hr-form-success"
        ref={successRef}
        className="relative bg-[#11161D] border-t border-white/[0.06]"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          {/* Check icon + eyebrow on a single horizontal row.
              The standalone Eyebrow component renders a leading
              hairline ("— REQUEST RECEIVED") that doesn't sit well
              next to the disc, so we inline the label here. */}
          <div className="inline-flex items-center gap-4">
            <span className="inline-grid place-items-center w-12 h-12 rounded-full bg-[#F4D64E]/[0.12] border border-[#F4D64E]/40 text-[#F4D64E]">
              <Check className="w-5 h-5" strokeWidth={2.5} />
            </span>
            <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]">
              {formCfg.success_eyebrow}
            </span>
          </div>
          <h2 className="mt-8 font-display text-[1.85rem] md:text-[2.5rem] font-black tracking-[-0.03em] leading-[1.05] text-white">
            {formCfg.success_headline}
          </h2>
          <p className="mt-6 max-w-[58ch] mx-auto text-[1rem] text-neutral-400 leading-[1.7]">
            {formCfg.success_body}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="review-form" data-testid="hr-form" className="relative bg-[#11161D] border-t border-white/[0.06]">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-3xl">
          <span data-reveal="eyebrow">
            <Eyebrow>{formCfg.eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2rem] md:text-[2.85rem] font-extrabold tracking-[-0.035em] leading-[1.04] text-white"
          >
            <span>
              {formCfg.headline_main}{" "}
              <span className="italic font-normal text-neutral-400">{formCfg.headline_italic}</span>
            </span>
          </h2>
          {formCfg.intro ? (
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-6 max-w-[58ch] text-[1rem] text-neutral-400 leading-[1.7]"
            >
              <span>{formCfg.intro}</span>
            </p>
          ) : null}
        </div>

        <form onSubmit={onSubmit} noValidate className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
          {/* Honeypot */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
            <label>
              Leave this field empty
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
                name="hp"
              />
            </label>
          </div>

          <FieldInput
            label={formCfg.label_name}
            name="name"
            required
            value={form.name}
            onChange={onChange}
            placeholder={formCfg.ph_name}
            testId="hr-input-name"
            hasError={show("name")}
            errorHint={errors.name}
          />
          <FieldInput
            label={formCfg.label_email}
            name="email"
            type="email"
            required
            value={form.email}
            onChange={onChange}
            placeholder={formCfg.ph_email}
            testId="hr-input-email"
            hasError={show("email")}
            errorHint={errors.email}
          />
          <div className="md:col-span-2">
            <FieldInput
              label={formCfg.label_website}
              name="website"
              type="url"
              required
              value={form.website}
              onChange={onChange}
              placeholder={formCfg.ph_website}
              testId="hr-input-website"
              hasError={show("website")}
              errorHint={errors.website}
            />
          </div>

          {/* Platform — single select pills */}
          {platformOptions.length ? (
            <div className="md:col-span-2">
              <span className="block text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-400 mb-4">
                {formCfg.label_platform}
                {req.platform ? <span className="ml-1 text-[#F4D64E]">*</span> : null}
              </span>
              <div className="flex flex-wrap gap-3">
                {platformOptions.map((opt) => {
                  const active = form.platform === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, platform: opt }))}
                      aria-pressed={active}
                      data-testid={`hr-platform-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className={`inline-flex items-center rounded-full px-5 md:px-6 py-3 md:py-3.5 text-[0.76rem] md:text-[0.8rem] font-bold tracking-[0.18em] uppercase border transition-all duration-300 ${
                        active
                          ? "bg-[#F4D64E] text-neutral-950 border-[#F4D64E]"
                          : "text-neutral-300 border-white/15 hover:border-[#F4D64E]/60 hover:text-[#F4D64E]"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Goals — multi-select pills */}
          {goalOptions.length ? (
            <div className="md:col-span-2">
              <span className="block text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-400 mb-2.5">
                {formCfg.label_goals}
                {req.goals ? <span className="ml-1 text-[#F4D64E]">*</span> : null}
              </span>
              {formCfg.help_goals ? (
                <p className="text-[0.82rem] text-neutral-500 leading-[1.55] mb-4">
                  {formCfg.help_goals}
                </p>
              ) : null}
              <div className={`flex flex-wrap gap-3 ${show("goals") ? "p-3 -m-3 rounded-lg bg-red-500/[0.04] ring-1 ring-red-500/20" : ""}`}>
                {goalOptions.map((opt) => {
                  const active = goals.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() =>
                        setGoals((prev) =>
                          prev.includes(opt)
                            ? prev.filter((g) => g !== opt)
                            : [...prev, opt]
                        )
                      }
                      aria-pressed={active}
                      data-testid={`hr-goal-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className={`inline-flex items-center rounded-full px-5 md:px-6 py-3 md:py-3.5 text-[0.76rem] md:text-[0.8rem] font-bold tracking-[0.18em] uppercase border transition-all duration-300 ${
                        active
                          ? "bg-white/[0.05] text-[#F4D64E] border-[#F4D64E]"
                          : "text-neutral-400 border-white/10 hover:border-white/30 hover:text-white"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Revenue (optional unless required) */}
          {revenueOptions.length ? (
            <div className="md:col-span-2">
              <span className="block text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-400 mb-2.5">
                {formCfg.label_revenue}
                {req.revenue ? (
                  <span className="ml-1 text-[#F4D64E]">*</span>
                ) : (
                  formCfg.revenue_optional_label ? (
                    <span className="ml-2 text-neutral-500 normal-case tracking-normal text-[0.72rem] font-semibold">
                      {formCfg.revenue_optional_label}
                    </span>
                  ) : null
                )}
              </span>
              <div className={`flex flex-wrap gap-3 ${show("revenue") ? "p-3 -m-3 rounded-lg bg-red-500/[0.04] ring-1 ring-red-500/20" : ""}`}>
                {revenueOptions.map((opt) => {
                  const active = form.revenue_range === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          revenue_range: f.revenue_range === opt ? "" : opt,
                        }))
                      }
                      aria-pressed={active}
                      data-testid={`hr-revenue-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className={`inline-flex items-center rounded-full px-5 md:px-6 py-3 md:py-3.5 text-[0.76rem] md:text-[0.8rem] font-bold tracking-[0.18em] uppercase border transition-all duration-300 ${
                        active
                          ? "bg-white/[0.05] text-[#F4D64E] border-[#F4D64E]"
                          : "text-neutral-400 border-white/10 hover:border-white/30 hover:text-white"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Notes / message */}
          <div className="md:col-span-2">
            <span className="flex items-center gap-2 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-400 mb-2.5">
              <MessageSquare className="w-3.5 h-3.5 text-[#F4D64E]" strokeWidth={2} />
              {formCfg.label_message}
              {req.message ? (
                <span className="ml-1 text-[#F4D64E]">*</span>
              ) : (
                formCfg.message_optional_label ? (
                  <span className="ml-2 text-neutral-500 normal-case tracking-normal text-[0.72rem] font-semibold">
                    {formCfg.message_optional_label}
                  </span>
                ) : null
              )}
            </span>
            <div
              className={`relative rounded-md transition-all duration-300 ${
                show("message")
                  ? "bg-red-500/[0.04] ring-1 ring-red-500/25"
                  : "bg-white/[0.025] focus-within:bg-white/[0.05] focus-within:ring-1 focus-within:ring-[#F4D64E]/25"
              }`}
            >
              <textarea
                ref={noteRef}
                name="message"
                rows={3}
                value={form.message}
                onChange={onChange}
                placeholder={formCfg.ph_message}
                data-testid="hr-input-message"
                className={`w-full bg-transparent border-0 border-b-[1.5px] px-4 py-4 text-white text-[1.05rem] md:text-[1.15rem] leading-[1.55] placeholder:text-neutral-500 focus:outline-none transition-colors resize-none rounded-md ${
                  show("message")
                    ? "border-red-400/70 focus:border-red-400"
                    : "border-white/15 focus:border-[#F4D64E]"
                }`}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="md:col-span-2 pt-4 space-y-6">
            {tried && !canSubmit ? (
              <div
                role="alert"
                data-testid="hr-form-error-banner"
                className="flex items-start gap-3 rounded-md border border-red-500/25 bg-red-500/[0.06] px-5 py-4 text-[0.92rem] text-red-200 leading-[1.5]"
              >
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" strokeWidth={2.25} />
                <div>
                  <div className="font-bold tracking-[0.01em] text-white">Almost there.</div>
                  <div className="mt-0.5 text-neutral-300">
                    Please complete the required fields above.
                  </div>
                </div>
              </div>
            ) : null}

            {submitError ? (
              <div
                role="alert"
                data-testid="hr-form-submit-error"
                className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/[0.07] px-5 py-4 text-[0.92rem] text-red-200 leading-[1.5]"
              >
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" strokeWidth={2.25} />
                <div>
                  <div className="font-bold tracking-[0.01em] text-white">Couldn&apos;t send right now.</div>
                  <div className="mt-0.5 text-neutral-300">{submitError}</div>
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-6">
              {formCfg.small_text ? (
                <p className="max-w-[40ch] text-[0.78rem] text-neutral-500 leading-[1.55]">
                  {formCfg.small_text}
                </p>
              ) : <span />}
              <PrimaryCTA testId="hr-submit-btn" type="submit" disabled={submitting}>
                {submitting ? formCfg.submit_loading : formCfg.submit_label}
              </PrimaryCTA>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

/* ============================================================
   08 · Final CTA
============================================================ */
const HrFinalCta = ({ final }) => {
  const trustItems = itemTitles(final.trust_items, []);
  const scrollToForm = () => {
    const el = document.getElementById("review-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <section data-testid="hr-final-cta" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 500px at 50% 30%, rgba(244,214,78,0.08), transparent 60%)",
        }}
      />
      <div className="relative max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
        <span data-reveal="eyebrow">
          <Eyebrow>{final.eyebrow}</Eyebrow>
        </span>
        <h2
          data-reveal="mask-block"
          style={{ "--i": 1 }}
          className="mt-7 font-display text-[2.1rem] md:text-[3rem] font-extrabold tracking-[-0.035em] leading-[1.04] text-white"
        >
          <span>
            {final.headline_main}{" "}
            <span className="italic font-normal text-neutral-400">{final.headline_italic}</span>
          </span>
        </h2>
        {final.body ? (
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="mt-7 max-w-[58ch] mx-auto text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.7]"
          >
            <span>{final.body}</span>
          </p>
        ) : null}
        <div data-reveal="eyebrow" style={{ "--i": 3 }} className="mt-10 flex flex-col items-center gap-5">
          <PrimaryCTA testId="hr-final-cta-btn" onClick={scrollToForm}>
            {final.cta_label}
          </PrimaryCTA>
          <TrustStrip items={trustItems} center />
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   Page composition
============================================================ */
export default function HomepageReviewPage() {
  const cms = useContent("homepage_review", HR_DEFAULTS);

  useSeo({
    route: "/homepage-review",
    fallback: {
      title: "Private Shopify Homepage Review — Sunday Digital",
      description:
        "Submit your Shopify store for a private homepage review. For the right stores, we may prepare a private homepage preview — with no initial commitment.",
    },
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Reveal observer
  useEffect(() => {
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;
    const els = root.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    const force = setTimeout(() => {
      els.forEach((el) => el.classList.add("is-revealed"));
    }, 1500);
    return () => {
      io.disconnect();
      clearTimeout(force);
    };
  }, []);

  return (
    <div data-variant="v5" data-testid="homepage-review-page" className="bg-[#0B0F14] text-white">
      <Nav />
      <main>
        <HrHero hero={cms.hero || HR_HERO_DEFAULTS} />
        <HrWhy why={cms.why || HR_WHY_DEFAULTS} />
        <HrReceive receive={cms.receive || HR_RECEIVE_DEFAULTS} />
        <HrNot not={cms.not || HR_NOT_DEFAULTS} />
        <HrHow how={cms.how || HR_HOW_DEFAULTS} />
        <HrWho who={cms.who || HR_WHO_DEFAULTS} />
        <HrForm formCfg={cms.form || HR_FORM_DEFAULTS} />
        <HrFinalCta final={cms.final || HR_FINAL_DEFAULTS} />
      </main>
      <Footer />
    </div>
  );
}
